import { useMutation } from '@tanstack/react-query'
import { enqueueSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { taskQueryKeys } from '@omnea-app/fe-tasks/queryKeys'
import { throwIfApiError } from '@omnea-app/legacy/utils/error-handling/throwifApiError'
import { RequestsHttpClient } from '@omnea-app/requests/requestsHttpClient'
import { RequestDetailQueryKey, RequestDetailsQueryKey } from '../../../useRequestDetail'

export interface Pause {
  assigneeId: string
  id: string
  start: string
  note: string | null
  pause_reason: {
    id: string
    text: string
  }
}

interface Props {
  onSuccess?: (data: Pause) => void
  onError?: (error: Error) => void
}

export const usePauseRequest = ({ onSuccess, onError }: Props) => {
  const { t } = useTranslation('requests')
  const { getToken } = useAuthenticatedUser()

  return useMutation({
    mutationFn: async ({
      requestId,
      assigneeId,
      note,
      reasonId,
      reminderDate,
    }: {
      requestId: string
      assigneeId: string
      note: string | null
      reasonId: string
      reminderDate: Date
    }) => {
      const apiClient = new RequestsHttpClient({ authorization: await getToken() })

      const { data, error } = await apiClient.POST('/{id}/pause', {
        body: { assigneeId, note, reasonId, reminderDate: reminderDate.toISOString() },
        params: { path: { id: requestId } },
      })

      throwIfApiError(error)

      return data
    },
    onSuccess: (data, { requestId }, _, { client }) => {
      void client.invalidateQueries({ queryKey: [RequestDetailQueryKey, requestId] })
      void client.invalidateQueries({ queryKey: ['dashboard-requests'] })
      void client.invalidateQueries({ queryKey: ['dashboard-requests-stats'] })
      void client.invalidateQueries({ queryKey: [RequestDetailsQueryKey] })
      void client.invalidateQueries({ queryKey: taskQueryKeys.all })
      onSuccess?.(data)
    },
    onError: (error) => {
      enqueueSnackbar(t('pause.errors.pause-toast'), { variant: 'error' })
      onError?.(error)
    },
  })
}
